import type { CSSProperties, FC, ReactNode } from 'react'
import type { ContactTopic } from '@/constants/contact'

import { useState } from 'react'
import { ArrowRight, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Scallop } from '@/components/generals/scallop'
import { SectionHeading } from '@/components/generals/section-heading'
import { SERVICES } from '@/constants/home'

interface ServicesProps {
    onAsk: (topic: ContactTopic) => void
}

export const Services: FC<ServicesProps> = ({ onAsk }): ReactNode => {
    const [open, setOpen] = useState<number | null>(0)

    return (
        <section id='services' aria-labelledby='services-title' className='relative bg-cream py-24 sm:py-32'>
            <Scallop className='text-cream' />

            <div className='mx-auto max-w-5xl px-5 sm:px-8'>
                <SectionHeading
                    id='services-title'
                    lines={['Everything your brand', 'needs to make noise']}
                    sticker='Services'
                    stickerShape='blob'
                    stickerColor='text-bubblegum'
                />

                <ul className='mt-16 space-y-5'>
                    {SERVICES.map((service, index) => {
                        const isOpen = open === index
                        const triggerId = `service-trigger-${index}`
                        const panelId = `service-panel-${index}`

                        return (
                            <li
                                key={service.title}
                                className={cn(
                                    'overflow-hidden rounded-[2rem] border-2 border-plum shadow-[0_5px_0_0_var(--color-plum)]',
                                    index % 2 === 0 ? 'reveal-left' : 'reveal-right',
                                    service.color
                                )}
                                style={{ '--reveal-delay': `${index * 110}ms` } as CSSProperties}
                            >
                                <h3>
                                    <button
                                        id={triggerId}
                                        type='button'
                                        aria-expanded={isOpen}
                                        aria-controls={panelId}
                                        onClick={() => setOpen(isOpen ? null : index)}
                                        className='group flex w-full items-center gap-4 px-5 py-5 text-left transition-colors duration-300 hover:bg-cream/40 sm:gap-6 sm:px-8 sm:py-6'
                                    >
                                        <span className='hidden font-heading text-2xl text-plum/50 transition-all duration-300 group-hover:-rotate-12 group-hover:scale-125 group-hover:text-coral sm:block'>
                                            {`0${index + 1}`}
                                        </span>

                                        <span className='min-w-0 flex-1'>
                                            <span className='block font-heading text-[clamp(1.6rem,3.4vw,2.6rem)] leading-none text-plum transition-transform duration-300 group-hover:translate-x-2'>
                                                {service.title}
                                            </span>

                                            <span className='mt-2 block text-sm text-plum/75 sm:text-base'>
                                                {service.tagline}
                                            </span>
                                        </span>

                                        <span
                                            className={cn(
                                                'grid size-11 shrink-0 place-items-center rounded-full border-2 border-plum text-plum transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_3px_0_0_var(--color-plum)] sm:size-12',
                                                isOpen ? 'bg-butter' : 'bg-cream group-hover:bg-butter'
                                            )}
                                        >
                                            <Plus
                                                aria-hidden='true'
                                                className={cn('size-5 transition-transform duration-300', isOpen && 'rotate-45')}
                                            />
                                        </span>
                                    </button>
                                </h3>

                                <div
                                    id={panelId}
                                    role='region'
                                    aria-labelledby={triggerId}
                                    inert={!isOpen}
                                    className={cn(
                                        'grid transition-[grid-template-rows] duration-300 ease-out',
                                        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                    )}
                                >
                                    <div className='overflow-hidden'>
                                        <div className='grid gap-6 border-t-2 border-plum/10 px-5 pt-6 pb-8 sm:px-8 md:grid-cols-[1.15fr_1fr] md:gap-10 md:pl-[5.25rem]'>
                                            <p className='leading-relaxed text-plum/85'>
                                                {service.description}
                                            </p>

                                            <div>
                                                <ul aria-label={`${service.title} includes`} className='flex flex-wrap gap-2'>
                                                    {service.tags.map((tag) => (
                                                        <li
                                                            key={tag}
                                                            className='rounded-full border-2 border-plum/20 bg-cream/70 px-3 py-1 text-sm font-medium text-plum'
                                                        >
                                                            {tag}
                                                        </li>
                                                    ))}
                                                </ul>

                                                <a
                                                    href='#contact'
                                                    onClick={() => onAsk(service.topic)}
                                                    className='mt-5 inline-flex items-center gap-2 font-heading text-lg text-plum underline decoration-2 underline-offset-4 hover:decoration-coral'
                                                >
                                                    Ask about {service.title.toLowerCase()}
                                                    <ArrowRight className='size-4' aria-hidden='true' />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}
